/**
 * ReportExportMenu — drop-in export menu (PDF / CSV / Share link) for any report.
 *
 * <ReportExportMenu type="channel" id={assessmentId} />
 * <ReportExportMenu type="audit" id={auditId} />
 */
import { useState, useRef, useEffect } from "react";
import { Download, FileText, FileSpreadsheet, Share2, Copy, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ReportExportMenu({ type, id, disabled, size = "default" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!id) return null;

  const download = async (fmt) => {
    setBusy(true);
    try {
      const url = `${API}/api/reports/export/${type}/${id}?format=${fmt}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      link.download = m ? m[1] : `report.${fmt}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${fmt.toUpperCase()} downloaded`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const createShare = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/reports/share/${type}/${id}`, {}, { withCredentials: true });
      const full = `${window.location.origin}${r.data.frontend_url}`;
      setShareUrl(full);
      navigator.clipboard.writeText(full).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Share link copied to clipboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link");
    } finally {
      setBusy(false);
    }
  };

  const sizeCls = size === "sm"
    ? "px-3 py-1.5 text-xs"
    : "px-4 py-2 text-sm";

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => setOpen((v) => !v)}
        data-testid={`report-export-toggle-${type}`}
        className={`inline-flex items-center gap-1.5 ${sizeCls} font-medium rounded-full bg-[#111111] text-white hover:bg-black disabled:opacity-50 transition-colors`}
      >
        <Download className="w-3.5 h-3.5" />
        Export
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 z-50 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)] overflow-hidden"
          data-testid={`report-export-menu-${type}`}
        >
          <button
            type="button"
            onClick={() => download("pdf")}
            disabled={busy}
            className="flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
            data-testid={`report-export-pdf-${type}`}
          >
            <FileText className="w-4 h-4 text-[#A855F7] mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#111111]">Download PDF</p>
              <p className="text-xs text-slate-500 mt-0.5">Polished report ready to share</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => download("csv")}
            disabled={busy}
            className="flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
            data-testid={`report-export-csv-${type}`}
          >
            <FileSpreadsheet className="w-4 h-4 text-[#A855F7] mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#111111]">Download CSV</p>
              <p className="text-xs text-slate-500 mt-0.5">Raw data for spreadsheets</p>
            </div>
          </button>

          <button
            type="button"
            onClick={createShare}
            disabled={busy}
            className="flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
            data-testid={`report-export-share-${type}`}
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
            ) : (
              <Share2 className="w-4 h-4 text-[#A855F7] mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#111111]">
                {copied ? "Link copied!" : "Get share link"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {shareUrl || "Public read-only URL"}
              </p>
            </div>
            {shareUrl && !copied && (
              <Copy
                className="w-3.5 h-3.5 text-slate-400 mt-0.5 cursor-pointer hover:text-[#A855F7]"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(shareUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                  toast.success("Copied");
                }}
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
